/**
 * Row-level notifications for the nested forms the cocoon gem renders.
 *
 * The client that adds and removes those rows
 * (`boot/main/globalSetup/nestedFields`) emits no events, so there is nothing
 * to subscribe to.
 *
 * Inserts are picked up with a `MutationObserver`: a row arriving in the page
 * is a DOM mutation and nothing else.
 *
 * Removes are picked up from a delegated `click` rather than from the same
 * observer, because only unsaved rows are detached. A persisted row keeps its
 * place with `_destroy` set to `"1"` and `display: none`, which a `childList`
 * observer never hears about. The click covers both kinds identically, and a
 * listener on `container` sits below the client's own document-level delegate
 * in the bubble path, so `onRemove` sees the row before anything touches it.
 */

import { delegate } from 'common/delegate';

/**
 * The remove links the gem renders. The client handles their clicks and
 * `observeCocoonRows` reports them, so both match exactly the same links.
 */
export const REMOVE_LINK_SELECTOR =
  '.remove_fields.dynamic, .remove_fields.existing';

type CocoonRowHandler = (row: Element) => void;

type CocoonRowOptions = {
  /**
   * Selector for the element that counts as one row. Keep it in sync with the
   * `wrapper_class:` passed to `link_to_remove_association` in the view — that
   * is what the client walks up to when a remove link is clicked.
   */
  rowSelector: string;
  onInsert: CocoonRowHandler;
  onRemove?: CocoonRowHandler;
};

/**
 * @param container Element containing every row of the nested form.
 * @returns A function that stops observing and unbinds the click listener.
 */
export default function observeCocoonRows(
  container: Element,
  { rowSelector, onInsert, onRemove }: CocoonRowOptions
): () => void {
  const observer = new MutationObserver(records => {
    records.forEach(record => {
      record.addedNodes.forEach(node => {
        // The row is built from an HTML string, so the added nodes also carry
        // the whitespace and Rails comments that surround it.
        if (node instanceof Element && node.matches(rowSelector)) {
          onInsert(node);
        }
      });
    });
  });

  // `subtree`, not plain `childList`: a new row is inserted before the element
  // holding the add link, so it lands as that element's *sibling* — not
  // necessarily a direct child of `container`.
  observer.observe(container, { childList: true, subtree: true });

  const stopListening = onRemove
    ? delegate(container, 'click', REMOVE_LINK_SELECTOR, (_event, link) => {
        const row = link.closest(rowSelector);
        if (row) onRemove(row);
      })
    : null;

  return () => {
    observer.disconnect();
    stopListening?.();
  };
}
